<template>
  <Modal @close="$emit('close')">
    <template #header>
      Transfer Variation Order
    </template>

    <template #body>
      <div class="space-y-4">
        <div
          v-if="variationOrder"
          class="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-3"
        >
          <div class="flex items-start justify-between gap-3">
            <div class="min-w-0">
              <p class="text-sm font-semibold text-slate-900">
                {{ variationOrder.code }}
              </p>

              <p class="text-sm text-slate-600 mt-0.5 break-words">
                {{ variationOrder.description }}
              </p>
            </div>

            <span
              class="inline-flex shrink-0 items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
              :style="{
                backgroundColor: variationOrder.statusColour,
                color: variationOrder.statusTextColour
              }"
            >
              {{ variationOrder.statusTitle }}
            </span>
          </div>

          <dl class="grid grid-cols-2 gap-3 text-sm">
            <div>
              <dt class="text-slate-500">
                Budget
              </dt>

              <dd class="font-medium text-slate-900">
                {{ australianCurrency(variationOrder.totalBudget) }}
              </dd>
            </div>

            <div v-if="variationOrder.client_ref">
              <dt class="text-slate-500">
                Reference
              </dt>

              <dd class="font-medium text-slate-900">
                {{ variationOrder.client_ref }}
              </dd>
            </div>
          </dl>
        </div>

        <form
          class="space-y-4"
          @submit.prevent="transfer"
        >
          <label class="block">
            <span class="block text-sm font-medium text-gray-700 mb-1">Target Project</span>

            <select
              v-model="targetProjectId"
              class="form-input w-full"
            >
              <option
                :value="undefined"
                disabled
              >
                Select a project
              </option>

              <option
                v-for="groupProject in targetProjects"
                :key="groupProject.id"
                :value="groupProject.id"
              >
                {{ groupProject.name }}
              </option>
            </select>
          </label>
        </form>
      </div>
    </template>

    <template #footer>
      <button
        type="button"
        class="btn btn--ghost-default"
        :disabled="transferAction.loading.value"
        @click="$emit('close')"
      >
        Cancel
      </button>

      <button
        type="button"
        class="btn btn--primary-blue"
        :disabled="!targetProjectId || transferAction.loading.value"
        @click="transfer"
      >
        Transfer
      </button>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import Modal from '@/components/Modal.vue';
import { useStoreApiAction } from '@/composables/useStoreApiAction';
import { australianCurrency } from '@/helpers';
import { useProjectGroupsStore } from '@/store/projectGroups';
import { useProjectsStore } from '@/store/projects';
import { useVariationOrdersStore } from '@/store/variationOrders';
import Swal from 'sweetalert2';
import { computed, ref } from 'vue';

const props = defineProps<{
  variationOrderId: Id;
  projectId: number;
}>();

const emit = defineEmits<{
  close: [];
  transferred: [];
}>();

const projectsStore = useProjectsStore();
const projectGroupsStore = useProjectGroupsStore();
const variationOrdersStore = useVariationOrdersStore();
const transferAction = useStoreApiAction(variationOrdersStore.transferVariationOrder);
const targetProjectId = ref<number | undefined>();
const project = computed(() => projectsStore.project);

const targetProjects = computed(() => {
  return projectGroupsStore.models.with('projects').find(project.value?.project_group_id)?.projects?.filter(
    (groupProject) => groupProject.id !== project.value?.id,
  ) ?? [];
});

const variationOrder = computed(() => {
  return variationOrdersStore.models.find(props.variationOrderId);
});

const transfer = async () => {
  if(!targetProjectId.value || transferAction.loading.value) {
    return;
  }

  try {
    await transferAction.request(props.projectId, props.variationOrderId, {
      target_project_id: targetProjectId.value,
    });

    Swal.fire({ icon: 'success', title: 'Variation order transferred' });
    emit('transferred');
    emit('close');
  } catch (error: any) {
    Swal.fire({
      icon: 'error',
      title: 'Transfer failed',
      text: error?.data?.message,
    });
  }
};
</script>
